import React, { useState, useMemo, useEffect } from "react";
import {
  Input,
  Select,
  Spin,
  Pagination,
  Tooltip,
  message,
  Row,
  Col,
  Button,
  Typography,
  Card,
  Flex,
  Descriptions,
} from "antd";
import {
  OrderedListOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  RightOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import ServiceStatus from "../../ServiceStatus";
import "./AgentTable.scss";
import OrderService from "../../../../utils/api/orders";
import UserService from "../../../../utils/api/users";

const { Option } = Select;
const { Title, Text } = Typography;

export default function AgentTable(props) {
  const { openOrders, loading, userId } = props;
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [searchedValue, setSearchedValue] = useState("");
  const [filterServiceCount, setFilterServiceCount] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [buyers, setBuyers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "descend",
  });

  useEffect(() => {
    if (userId && !openOrders) {
      fetchAssignedData();
    } else if (openOrders) {
      setOrders(openOrders);
    }
  }, [userId, openOrders]);

  useEffect(() => {
    fetchSelectors();
  }, []);

  const fetchSelectors = async () => {
    try {
      const response = await UserService.GetUserSelectors();
      setBuyers(response.data.buyers || []);
      setSellers(response.data.sellers || []);
    } catch (error) {
      message.error("Error al cargar los selectores de usuario");
    }
  };

  const fetchAssignedData = async () => {
    try {
      setInternalLoading(true);
      const ordersResponse = await OrderService.GetAssignedOrders(userId);
      let ordersData = ordersResponse?.data?.orders || [];

      if (ordersData.length > 0) {
        const servicesResponse = await OrderService.GetAssignedServices(userId);
        const servicesData = servicesResponse?.data?.services || [];
        if (servicesData.length > 0) {
          const ordersWithServices = ordersData.map((order) => {
            const orderServices = servicesData.filter(
              (service) => service.order_id?.toString() === order.id?.toString()
            );

            return {
              ...order,
              orderId: order.id,
              services:
                orderServices.length > 0 ? orderServices : order.services || [],
            };
          });

          setOrders(ordersWithServices);
        } else {
          const ordersWithOrderId = ordersData.map((order) => ({
            ...order,
            orderId: order.id,
          }));
          setOrders(ordersWithOrderId);
        }
      } else {
        const servicesResponse = await OrderService.GetAssignedServices(userId);
        const servicesData = servicesResponse?.data?.services || [];

        if (servicesData.length > 0) {
          const ordersMap = {};

          servicesData.forEach((service) => {
            const orderId = service.order_id?.toString() || "sin-orden";

            if (!ordersMap[orderId]) {
              ordersMap[orderId] = {
                orderId: orderId,
                id: orderId,
                title: `Orden ${orderId}`,
                client_bussiness: "Cliente",
                client_name: "Cliente",
                created_at: service.created_at || new Date().toISOString(),
                updated_at: service.updated_at || new Date().toISOString(),
                services: [],
              };
            }

            ordersMap[orderId].services.push(service);
          });

          const virtualOrders = Object.values(ordersMap);
          setOrders(virtualOrders);
        } else {
          setOrders([]);
          message.warning("No se encontraron órdenes ni servicios asignados");
        }
      }
    } catch (error) {
      message.error("Error al cargar las órdenes");
      setOrders([]);
    } finally {
      setInternalLoading(false);
    }
  };

  const dataToUse = openOrders || orders;
  const isLoading = loading !== undefined ? loading : internalLoading;

  const getServiceBadgeColor = (count) => {
    switch (count) {
      case 1:
        return "#77AFF8";
      case 2:
        return "#296CF2";
      case 3:
      default:
        return "#343BBF";
    }
  };

  const getServiceBadgeClass = (count) => {
    return `service-badge service-count-${count}`;
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "descend"
          ? "ascend"
          : "descend",
    }));
    setCurrentPage(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "descend" ? (
      <ArrowDownOutlined />
    ) : (
      <ArrowUpOutlined />
    );
  };

  const tableData = useMemo(() => {
    let filtered = dataToUse || [];

    if (searchedValue) {
      filtered = filtered.filter(
        (order) =>
          (order.orderId || order.id)?.toString().includes(searchedValue) ||
          order.title?.toLowerCase().includes(searchedValue.toLowerCase()) ||
          order.client_bussiness
            ?.toLowerCase()
            .includes(searchedValue.toLowerCase())
      );
    }

    if (filterServiceCount !== null) {
      filtered = filtered.filter((order) => {
        const count = (order.services || []).length;
        if (filterServiceCount === "1") return count === 1;
        if (filterServiceCount === "2") return count === 2;
        if (filterServiceCount === "3+") return count >= 3;
        return true;
      });
    }

    if (selectedBuyer) {
      filtered = filtered.filter((order) => order.buyerId === selectedBuyer);
    }

    if (selectedSeller) {
      filtered = filtered.filter((order) => order.sellerId === selectedSeller);
    }

    let mappedData = filtered.map((order, index) => {
      let latestServiceUpdate = null;
      let latestServiceUpdateFromNow = "";

      if (order.services && order.services.length > 0) {
        const serviceDates = order.services
          .map((service) => {
            const serviceDate =
              service.updated_at ||
              service.updatedAt ||
              service.created_at ||
              service.createdAt;
            return serviceDate ? moment(serviceDate).valueOf() : null;
          })
          .filter((date) => date !== null);

        if (serviceDates.length > 0) {
          latestServiceUpdate = Math.max(...serviceDates);
          latestServiceUpdateFromNow = moment(latestServiceUpdate).fromNow();
        }
      }

      if (!latestServiceUpdate) {
        latestServiceUpdate = moment(
          order.updated_at ||
            order.updatedAt ||
            order.created_at ||
            order.createdAt
        ).valueOf();
        latestServiceUpdateFromNow = moment(latestServiceUpdate).fromNow();
      }

      return {
        key: index,
        order_id: (order.orderId || order.id)?.toString() || `orden-${index}`,
        order_title: order.title?.toLowerCase() || "",
        client_bussiness:
          order.client_bussiness?.toLowerCase() ||
          order.client_name?.toLowerCase() ||
          "",
        updated_at: latestServiceUpdate,
        updated_at_fromNow: latestServiceUpdateFromNow,
        created_at: moment(order.created_at || order.createdAt).valueOf(),
        services: order.services || [],
        service_count: (order.services || []).length,
        originalOrder: order,
      };
    });

    if (sortConfig.key) {
      mappedData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (
          sortConfig.key === "created_at" ||
          sortConfig.key === "updated_at"
        ) {
          if (sortConfig.direction === "descend") {
            return bValue - aValue;
          } else {
            return aValue - bValue;
          }
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascend" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascend" ? 1 : -1;
        }
        return 0;
      });
    }

    return mappedData;
  }, [
    dataToUse,
    searchedValue,
    filterServiceCount,
    selectedBuyer,
    selectedSeller,
    sortConfig,
  ]);

  const resetFilters = () => {
    setSearchedValue("");
    setCurrentPage(1);
    setSelectedBuyer(null);
    setSelectedSeller(null);
    setFilterServiceCount(null);
    setSortConfig({
      key: "created_at",
      direction: "descend",
    });
  };

  const handleRowClick = (orderId) => {
    if (!orderId) return;
    navigate(`/orders/details/new/${orderId}`);
  };

  const toggleServicesDetails = (orderId) => {
    setExpandedRows((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handlePageChange = (page, newPageSize) => {
    setCurrentPage(page);
    if (newPageSize) {
      setPageSize(newPageSize);
    }
  };

  const paginatedOrders = tableData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const getTitle = () => {
    if (isLoading) return "Cargando órdenes...";
    if (userId && !openOrders) return "Órdenes Asignadas";
    return "Órdenes Generales";
  };

  return (
    <div className="agent-table-container">
      <Row gutter={[24, 16]}>
        <Col xs={24}>
          <Title level={3}>{getTitle()}</Title>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            value={filterServiceCount}
            onChange={(val) => setFilterServiceCount(val)}
            style={{ width: "100%" }}
            placeholder={
              <span>
                <OrderedListOutlined style={{ marginRight: 8 }} />
                Servicios
              </span>
            }
          >
            <Option value={null}>Todos los servicios</Option>
            <Option value="1">1 servicio</Option>
            <Option value="2">2 servicios</Option>
            <Option value="3+">3 o más servicios</Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={8} lg={12}>
          <Input
            placeholder="Buscar por número de orden, título o cliente..."
            value={searchedValue}
            onChange={(e) => setSearchedValue(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            onClear={() => setSearchedValue("")}
          />
        </Col>

        {(searchedValue ||
          filterServiceCount !== null ||
          selectedBuyer ||
          selectedSeller) && (
          <Col xs={24} sm={12} md={8} lg={6}>
            <Button onClick={resetFilters} block>
              Restablecer Filtros
            </Button>
          </Col>
        )}

        {/* Tabla */}
        <Col xs={24}>
          <div className="agent-table-wrapper">
            {isLoading ? (
              <div className="loading-container">
                <Spin size="large" />
                <p>Cargando órdenes...</p>
              </div>
            ) : (
              <table className="agent-styled-table">
                <thead>
                  <tr>
                    <th className="header-cell orden">Orden</th>
                    <th className="header-cell servicios">Servicios</th>
                    <th className="header-cell ver-servicios">Ver servicios</th>
                    <th className="header-cell titulo">Título</th>
                    <th className="header-cell cliente">Cliente</th>
                    <th
                      className="header-cell actualizacion sortable-header"
                      onClick={() => handleSort("updated_at")}
                    >
                      <span className="sortable-header-content">
                        Última Actualización {getSortIcon("updated_at")}
                      </span>
                    </th>
                    <th
                      className="header-cell antiguedad sortable-header"
                      onClick={() => handleSort("created_at")}
                    >
                      <span className="sortable-header-content">
                        Fecha de Creación {getSortIcon("created_at")}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-state-cell">
                        {searchedValue ||
                        filterServiceCount !== null ||
                        selectedBuyer ||
                        selectedSeller ? (
                          <div className="no-results">
                            <p>
                              No se encontraron órdenes con los filtros
                              aplicados
                            </p>
                            <button
                              className="clear-filters-btn empty-state-btn"
                              onClick={resetFilters}
                            >
                              Limpiar filtros
                            </button>
                          </div>
                        ) : userId ? (
                          <p>No hay órdenes asignadas disponibles</p>
                        ) : (
                          <p>No hay órdenes disponibles</p>
                        )}
                      </td>
                    </tr>
                  ) : (
                    paginatedOrders.map((order) => {
                      const isExpanded = expandedRows.includes(order.order_id);
                      return (
                      <React.Fragment key={order.order_id}>
                        <tr
                          className="table-row"
                          onClick={() => handleRowClick(order.order_id)}
                        >
                          <td className="body-cell orden">
                            <Link
                              to={`/orders/details/new/${order.order_id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="order-link"
                            >
                              {order.order_id}
                            </Link>
                          </td>
                          <td className="body-cell servicios">
                            <div
                              className={`service-badge ${getServiceBadgeClass(
                                order.service_count
                              )}`}
                              style={{
                                backgroundColor: getServiceBadgeColor(
                                  order.service_count
                                ),
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: 0,
                              }}
                            >
                              <span>{order.service_count}</span>
                            </div>
                          </td>
                          <td className="body-cell ver-servicios">
                            <div
                              className="services-toggle"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleServicesDetails(order.order_id);
                              }}
                              title={isExpanded ? "Ocultar servicios" : "Ver servicios"}
                            >
                              <span>Ver</span>
                              {isExpanded ? <DownOutlined /> : <RightOutlined />}
                            </div>
                          </td>
                          <td className="body-cell titulo">
                            {order.order_title.toUpperCase()}
                          </td>
                          <td className="body-cell cliente">
                            {order.client_bussiness.toUpperCase()}
                          </td>
                          <td className="body-cell actualizacion">
                            <Tooltip
                              title={moment(order.updated_at).format(
                                "DD/MM/YYYY HH:mm"
                              )}
                            >
                              {order.updated_at_fromNow}
                            </Tooltip>
                          </td>
                          <td className="body-cell antiguedad">
                            {moment(order.created_at).format("DD/MM/YYYY")}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="expanded-row">
                            <td colSpan="7" className="expanded-cell">
                              <div className="expanded-content">
                                <div className="services-section">
                                  <div className="section-title">
                                    Servicios de la Orden {order.order_id}
                                  </div>
                                  <div className="services-list">
                                    {order.services.map((service, idx) => (
                                      <div
                                        key={idx}
                                        className="service-item"
                                        onClick={() =>
                                          navigate(`/orders/details/new/${order.order_id}`)
                                        }
                                      >
                                        <Card
                                          size="small"
                                          className="service-card"
                                          bordered={true}
                                        >
                                          <Flex
                                            justify="space-between"
                                            align="center"
                                            className="service-header"
                                          >
                                            <Text
                                              code
                                              strong
                                              className="service-id"
                                            >
                                              #
                                              {service.serviceId ||
                                                service.id ||
                                                `SER-${idx + 1}`}
                                            </Text>
                                            <ServiceStatus
                                              status={service.status}
                                            />
                                          </Flex>

                                          <Descriptions
                                            column={1}
                                            size="small"
                                            className="service-details"
                                          >
                                            <Descriptions.Item>
                                              <Title
                                                level={5}
                                                className="service-title"
                                              >
                                                {service.title ||
                                                  "Servicio sin título"}
                                              </Title>
                                            </Descriptions.Item>

                                            {service.description && (
                                              <Descriptions.Item label="Descripción">
                                                <Text className="service-description">
                                                  {service.description}
                                                </Text>
                                              </Descriptions.Item>
                                            )}
                                          </Descriptions>

                                          <Flex
                                            justify="flex-end"
                                            className="service-footer"
                                          >
                                            <Text
                                              type="secondary"
                                              className="service-updated"
                                            >
                                              <ClockCircleOutlined
                                                style={{ marginRight: 4 }}
                                              />
                                              Actualizado:{" "}
                                              {moment(
                                                service.updated_at ||
                                                  service.updatedAt
                                              ).fromNow()}
                                            </Text>
                                          </Flex>
                                        </Card>
                                      </div>
                                    ))}
                                    {order.services.length === 0 && (
                                      <div className="no-services">
                                        No hay servicios en esta orden
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </Col>

        {/* Paginación */}
        {tableData.length > 0 && (
          <Col xs={24}>
            <div className="agent-pagination-container">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={tableData.length}
                showSizeChanger={true}
                pageSizeOptions={["10", "20", "50", "100"]}
                showQuickJumper={false}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} de ${total} órdenes`
                }
                onChange={handlePageChange}
                onShowSizeChange={handlePageChange}
              />
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
}
